import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtEUR } from "../lib/format";

const MESI_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtMese = (m) => `${MESI_IT[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(0, 4)}`;
const parseImp = (v) => parseFloat(String(v).replace(",", "."));
const MESE_ORA = new Date().toISOString().slice(0, 7);
const AUTO_DA = "2026-09";     // da qui: maturato atteso/reale + fisso
const STORICO_DA = "2024-07";  // da qui: versato registrabile
const STORICO_A = "2026-08";

const inp = "border border-slate-300 rounded-md px-2.5 py-2 text-sm";

// Cella VERSATO: modificabile dal master. 0 versamenti -> crea, 1 -> modifica,
// >1 -> il totale resta in lettura e i singoli record si gestiscono dal dettaglio (⋯).
const VersatoCell = ({ storeId, mese, pid, row, canEdit, onSaved, onToggleDet }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const recs = row.versamenti || [];
  const save = async () => {
    const importo = parseImp(val);
    if (isNaN(importo)) { toast.error("Importo non valido"); return; }
    try {
      if (recs.length === 1) await api.patch(`/versamenti/${recs[0].id}`, { importo });
      else await api.post("/versamenti", { store_id: storeId, partecipante_id: pid, mese, importo, note: "" });
      toast.success("Versato aggiornato");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canEdit) {
    return <span data-testid={`versato-${pid}-${mese}`} className="font-mono tnum">{fmtEUR(row.versato)}</span>;
  }
  if (recs.length > 1) {
    return (
      <button data-testid={`versato-multi-${pid}-${mese}`} onClick={onToggleDet}
        title="Più versamenti in questo mese: gestiscili dal dettaglio"
        className="font-mono tnum rounded px-1.5 -mx-1.5 hover:bg-indigo-50 transition-colors duration-150 cursor-pointer underline decoration-dotted">
        {fmtEUR(row.versato)}
      </button>
    );
  }
  if (!editing) {
    return (
      <button data-testid={`versato-edit-${pid}-${mese}`} title="Clicca per modificare il versato"
        onClick={() => { setVal(row.versato ? String(row.versato).replace(".", ",") : ""); setEditing(true); }}
        className="font-mono tnum rounded px-1.5 -mx-1.5 hover:bg-indigo-50 hover:text-indigo-700 transition-colors duration-150 cursor-pointer">
        {fmtEUR(row.versato)}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input data-testid={`versato-input-${pid}-${mese}`} autoFocus value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        className="w-24 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-mono" />
      <button data-testid={`versato-save-${pid}-${mese}`} onClick={save}
        className="text-emerald-600 font-bold px-1 hover:text-emerald-800">✓</button>
    </span>
  );
};

// Fisso di gestione del mese (solo master, solo mesi >= set 2026): vuoto = standard.
const FissoCell = ({ storeId, mese, pid, row, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const custom = row.fisso != null && row.fisso_default != null && row.fisso !== row.fisso_default;
  const save = async () => {
    const importo = val.trim() === "" ? null : parseImp(val);
    if (importo !== null && isNaN(importo)) { toast.error("Importo non valido"); return; }
    try {
      await api.put("/quote/fisso", { store_id: storeId, mese, importo });
      toast.success(importo === null ? "Mese tornato al fisso standard" : "Fisso del mese aggiornato");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!editing) {
    return (
      <button data-testid={`fisso-edit-${pid}-${mese}`}
        title="Fisso di gestione di questo mese — clicca per modificare (vuoto = standard)"
        onClick={() => { setVal(String(row.fisso ?? "").replace(".", ",")); setEditing(true); }}
        className={`font-mono tnum rounded px-1.5 -mx-1.5 hover:bg-indigo-50 transition-colors duration-150 cursor-pointer ${custom ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
        {fmtEUR(row.fisso)}{custom ? " *" : ""}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input data-testid={`fisso-input-${pid}-${mese}`} autoFocus value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        className="w-20 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-mono" />
      <button data-testid={`fisso-save-${pid}-${mese}`} onClick={save}
        className="text-emerald-600 font-bold px-1 hover:text-emerald-800">✓</button>
    </span>
  );
};

const VersamentiDettaglio = ({ pid, mese, recs, onChanged }) => {
  const del = async (id) => {
    if (!window.confirm("Eliminare questo versamento?")) return;
    try { await api.delete(`/versamenti/${id}`); toast.success("Versamento eliminato"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <tr data-testid={`versamenti-dettaglio-${pid}-${mese}`}>
      <td colSpan={10} className="px-4 pb-2 pt-0">
        <div className="bg-slate-50 rounded-lg p-2.5 space-y-1.5">
          {recs.map((v) => (
            <div key={v.id} data-testid={`versamento-row-${v.id}`} className="flex items-center gap-3 text-xs">
              <span className="font-mono tnum font-semibold">{fmtEUR(v.importo)}</span>
              <span className="text-slate-500">{v.note || "—"}</span>
              <button data-testid={`versamento-delete-${v.id}`} onClick={() => del(v.id)}
                className="text-red-500 hover:text-red-700 font-semibold ml-auto transition-colors">Elimina</button>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
};

export default function QuoteSociPage() {
  const { stores, storeId, isMaster } = useOutletContext();
  const figli = stores.filter((s) => s.tipo === "figlio");
  // Store dalla barra globale; il periodo è gestito dal selettore Da→A di questa pagina
  const effSel = storeId !== "all" ? storeId : figli[0]?.id;
  const [daMese, setDaMese] = useState(MESE_ORA);
  const [aMese, setAMese] = useState(MESE_ORA);
  const [data, setData] = useState(null);
  const [totali, setTotali] = useState(null);
  const [openDet, setOpenDet] = useState({});
  const [espansi, setEspansi] = useState({});
  const [vers, setVers] = useState({ partecipante_id: "", mese: MESE_ORA, importo: "", note: "" });

  const da = daMese <= aMese ? daMese : aMese;
  const a = daMese <= aMese ? aMese : daMese;
  const singolo = da === a;

  const load = () => {
    if (!effSel) return;
    api.get(`/quote?store_id=${effSel}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (isMaster) api.get("/quote/totali").then((r) => setTotali(r.data)).catch(() => {});
  };
  useEffect(() => { setData(null); load(); }, [effSel]);

  const addVers = async () => {
    try {
      await api.post("/versamenti", {
        store_id: effSel, partecipante_id: vers.partecipante_id || null, mese: vers.mese,
        importo: parseImp(vers.importo), note: vers.note,
      });
      toast.success("Versamento registrato");
      setVers({ partecipante_id: "", mese: a, importo: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleDet = (k) => setOpenDet((s) => ({ ...s, [k]: !s[k] }));
  const toggleEsp = (pid) => setEspansi((s) => ({ ...s, [pid]: !s[pid] }));

  const meseCorrente = data?.mese_corrente || MESE_ORA;
  const isAutoMese = (m) => m >= AUTO_DA && m <= meseCorrente;
  const isStoricoMese = (m) => m >= STORICO_DA && m <= STORICO_A;
  const fuoriPerimetro = singolo && !isAutoMese(da) && !isStoricoMese(da);

  // Mesi dell'intervallo presenti nei dati del partecipante (storico + automatici)
  const mesiDi = (p) => {
    if (singolo) {
      const m = [...(p.storico || []), ...(p.mesi || [])].find((x) => x.mese === da);
      return m ? [m] : [];
    }
    return [...(p.storico || []), ...(p.mesi || [])]
      .filter((x) => x.mese >= da && x.mese <= a)
      .sort((x, y) => x.mese.localeCompare(y.mese));
  };

  const totDi = (p) => {
    const mm = mesiDi(p);
    return {
      versato: mm.reduce((s, m) => s + m.versato, 0),
      atteso: mm.filter((m) => isAutoMese(m.mese)).reduce((s, m) => s + (m.maturato_atteso || 0), 0),
      reale: mm.filter((m) => isAutoMese(m.mese)).reduce((s, m) => s + (m.maturato_reale || 0), 0),
      mm,
    };
  };

  const legacyInRange = (data?.versamenti_legacy || []).filter((v) => v.mese >= da && v.mese <= a);
  const versatoIntervallo = useMemo(() => {
    if (!data) return 0;
    return data.partecipanti.reduce((acc, p) => acc + totDi(p).versato, 0)
      + legacyInRange.reduce((s, v) => s + v.importo, 0);
  }, [data, da, a]);

  const hasCalc = !data?.da_definire;
  const mostraMaturato = hasCalc && (singolo ? isAutoMese(da) : (data?.partecipanti || [])
    .some((p) => totDi(p).mm.some((m) => isAutoMese(m.mese))));
  const storeNome = data?.store?.nome || stores.find((s) => s.id === effSel)?.nome || "";

  return (
    <div data-testid="quote-soci-page" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Quote soci — {storeNome}</h1>
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Da</label>
          <input data-testid="quote-da-mese" type="month" className={inp} value={daMese} onChange={(e) => setDaMese(e.target.value)} />
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">A</label>
          <input data-testid="quote-a-mese" type="month" className={inp} value={aMese} onChange={(e) => setAMese(e.target.value)} />
        </div>
      </div>

      <p data-testid="quote-note-formule" className="text-sm text-slate-500 -mt-2">
        Il <span className="font-semibold text-slate-700">maturato reale</span> (quanto verrà pagato) usa l'utile
        realizzato del mese; il <span className="font-semibold text-slate-700">maturato atteso</span> usa l'utile
        come se tutti gli ordini andassero a buon fine. Il fisso di gestione è sottratto prima della divisione per quote.
      </p>

      {isMaster && (
        <div data-testid="quote-totale-tutti" className="card p-5 fade-in">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Versato a tutti i soci — {singolo ? fmtMese(da) : `${fmtMese(da)} → ${fmtMese(a)}`}
              </div>
              <div data-testid="quote-totale-intervallo" className="font-heading text-3xl font-bold text-slate-900 tabular-nums mt-1">
                {fmtEUR(versatoIntervallo)}
              </div>
            </div>
            {totali && (
              <div className="text-xs text-slate-400 font-mono tnum pb-1" data-testid="quote-totale-tutti-valore">
                Di sempre, tutti gli store: {fmtEUR(totali.totale_versato)}
              </div>
            )}
          </div>
        </div>
      )}

      {!isMaster && data?.partecipanti?.[0] && (
        <div data-testid="quote-totale-socio" className="card p-5 fade-in">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Totale versato — dal luglio 2024 a oggi</div>
          <div data-testid="quote-totale-socio-valore" className="font-heading text-3xl font-bold text-emerald-700 tabular-nums mt-1">
            {fmtEUR(data.partecipanti[0].totale_versato)}
          </div>
        </div>
      )}

      {data?.da_definire && (
        <div data-testid="ripartizione-da-definire-banner" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
          <p className="font-semibold mb-1">Ripartizione incompleta — quote non calcolate</p>
          <p>
            {isMaster
              ? "La ripartizione delle quote tra i partecipanti va completata in Configurazione store (sezione Ripartizione utile). Finché resta incompleta, le quote maturate non vengono calcolate né mostrate. I versati restano registrabili."
              : "La ripartizione delle quote è in fase di definizione da parte dell'amministratore. Le quote maturate saranno visibili quando sarà completata."}
          </p>
        </div>
      )}

      {!data && (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="skeleton h-28" />)}</div>
      )}

      {data && data.partecipanti.length === 0 && (
        <div className="card p-6 text-sm text-slate-500">
          {isMaster
            ? "Questo store non ha una ripartizione configurata. Impostala in Configurazione → Ripartizione utile."
            : "Nessuna quota è ancora collegata al tuo utente. Contatta l'amministratore."}
        </div>
      )}

      {data && data.partecipanti.length > 0 && fuoriPerimetro && (
        <div data-testid="quote-mese-vuoto" className="card p-6 text-sm text-slate-500">
          Nessun dato per questo mese: i versati partono da luglio 2024, i maturati da settembre 2026 e i mesi futuri non sono mostrati.
        </div>
      )}

      {data && data.partecipanti.length > 0 && !fuoriPerimetro && (
        <div className="card overflow-hidden fade-in">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="quote-table">
              <thead>
                <tr className="bg-slate-50/80 text-left">
                  <th className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Partecipante{singolo ? ` — ${fmtMese(da)}` : ""}
                  </th>
                  {singolo && isAutoMese(da) && isMaster && (
                    <th className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 text-right" title="Fisso di gestione del mese (solo master, modificabile)">Fisso</th>
                  )}
                  {mostraMaturato && <th className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 text-right" title="Quota se tutti gli ordini andassero a buon fine">Maturato atteso</th>}
                  {mostraMaturato && <th className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 text-right" title="Quota sui soldi davvero entrati: è quanto verrà pagato">Maturato reale</th>}
                  <th className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Versato</th>
                </tr>
              </thead>
              <tbody>
                {data.partecipanti.map((p) => {
                  const t = totDi(p);
                  if (t.mm.length === 0) return null;
                  const detAperto = espansi[p.partecipante_id];
                  const righe = [
                    <tr key={p.partecipante_id} data-testid={`quote-row-${p.partecipante_id}`}
                      className={`border-t border-slate-100 transition-colors duration-150 ${singolo ? "" : "cursor-pointer hover:bg-indigo-50/40"}`}
                      onClick={singolo ? undefined : () => toggleEsp(p.partecipante_id)}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {!singolo && <span className="text-slate-400 text-xs w-3">{detAperto ? "▾" : "▸"}</span>}
                          <span className="font-semibold text-slate-800">{p.nome}</span>
                          <span className={`text-[10px] rounded-full px-2 py-0.5 border ${p.tipo === "proprietario" ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{p.tipo}</span>
                          {isMaster && <span className="text-xs text-slate-400 font-mono">{p.quote} quote</span>}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono tnum mt-0.5">
                          Totale versato di sempre: <span data-testid={`totale-versato-${p.partecipante_id}`} className="text-slate-600 font-semibold">{fmtEUR(p.totale_versato)}</span>
                        </div>
                      </td>
                      {singolo && isAutoMese(da) && isMaster && (
                        <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                          <FissoCell storeId={effSel} mese={da} pid={p.partecipante_id} row={t.mm[0]} onSaved={load} />
                        </td>
                      )}
                      {mostraMaturato && (
                        <td className={`px-4 py-2.5 font-mono text-right tnum ${t.atteso < 0 ? "text-red-600" : ""}`} data-testid={`maturato-atteso-${p.partecipante_id}`}>
                          {singolo && !isAutoMese(da) ? "—" : fmtEUR(t.atteso)}
                        </td>
                      )}
                      {mostraMaturato && (
                        <td className={`px-4 py-2.5 font-mono text-right tnum ${t.reale < 0 ? "text-red-600" : ""}`} data-testid={`maturato-reale-${p.partecipante_id}`}>
                          {singolo && !isAutoMese(da) ? "—" : fmtEUR(t.reale)}
                        </td>
                      )}
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {singolo ? (
                          <>
                            <VersatoCell storeId={effSel} mese={da} pid={p.partecipante_id} row={t.mm[0]}
                              canEdit={isMaster} onSaved={load} onToggleDet={() => toggleDet(`${p.partecipante_id}-${da}`)} />
                            {isMaster && (t.mm[0].versamenti || []).length > 0 && (
                              <button data-testid={`versamenti-toggle-${p.partecipante_id}-${da}`} onClick={() => toggleDet(`${p.partecipante_id}-${da}`)}
                                className="ml-1 text-slate-300 hover:text-slate-500 text-xs align-middle" title="Dettaglio versamenti">⋯</button>
                            )}
                          </>
                        ) : (
                          <span data-testid={`versato-intervallo-${p.partecipante_id}`} className="font-mono tnum font-semibold text-slate-900">{fmtEUR(t.versato)}</span>
                        )}
                      </td>
                    </tr>,
                  ];
                  // dettaglio singoli versamenti (vista singolo mese)
                  if (singolo && isMaster && openDet[`${p.partecipante_id}-${da}`] && (t.mm[0].versamenti || []).length > 0) {
                    righe.push(<VersamentiDettaglio key={`vdet-${p.partecipante_id}`} pid={p.partecipante_id} mese={da} recs={t.mm[0].versamenti} onChanged={load} />);
                  }
                  // dettaglio mese per mese (vista intervallo)
                  if (!singolo && detAperto) {
                    t.mm.forEach((m) => {
                      const auto = isAutoMese(m.mese);
                      righe.push(
                        <tr key={`${p.partecipante_id}-${m.mese}`} data-testid={`quote-det-${p.partecipante_id}-${m.mese}`} className="border-t border-slate-50 bg-slate-50/40">
                          <td className="px-4 py-1.5 pl-9 text-sm text-slate-600">{fmtMese(m.mese)}
                            {auto && isMaster && m.fisso != null && (
                              <span className="ml-2 text-[11px] text-slate-400 font-mono">fisso {fmtEUR(m.fisso)}</span>
                            )}
                          </td>
                          {mostraMaturato && <td className="px-4 py-1.5 font-mono text-right tnum text-slate-500 text-xs">{auto ? fmtEUR(m.maturato_atteso) : "—"}</td>}
                          {mostraMaturato && <td className="px-4 py-1.5 font-mono text-right tnum text-slate-500 text-xs">{auto ? fmtEUR(m.maturato_reale) : "—"}</td>}
                          <td className="px-4 py-1.5 text-right">
                            <VersatoCell storeId={effSel} mese={m.mese} pid={p.partecipante_id} row={m}
                              canEdit={isMaster} onSaved={load} onToggleDet={() => toggleDet(`${p.partecipante_id}-${m.mese}`)} />
                            {isMaster && (m.versamenti || []).length > 0 && (
                              <button data-testid={`versamenti-toggle-${p.partecipante_id}-${m.mese}`} onClick={() => toggleDet(`${p.partecipante_id}-${m.mese}`)}
                                className="ml-1 text-slate-300 hover:text-slate-500 text-xs align-middle" title="Dettaglio versamenti">⋯</button>
                            )}
                          </td>
                        </tr>
                      );
                      if (isMaster && openDet[`${p.partecipante_id}-${m.mese}`] && (m.versamenti || []).length > 0) {
                        righe.push(<VersamentiDettaglio key={`vdet-${p.partecipante_id}-${m.mese}`} pid={p.partecipante_id} mese={m.mese} recs={m.versamenti} onChanged={load} />);
                      }
                    });
                  }
                  return righe;
                })}
              </tbody>
            </table>
          </div>
          {!singolo && (
            <p className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">
              Totali dell'intervallo; i maturati contano solo i mesi da settembre 2026. Clicca un partecipante per il dettaglio mese per mese.
            </p>
          )}
        </div>
      )}

      {isMaster && data && data.partecipanti.length > 0 && (
        <div className="card p-5 max-w-2xl">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Registra versamento</h2>
          <div className="flex flex-wrap gap-2">
            <select data-testid="versamento-partecipante" className={inp} value={vers.partecipante_id}
              onChange={(e) => setVers({ ...vers, partecipante_id: e.target.value })}>
              <option value="">Partecipante…</option>
              {data.partecipanti.map((p) => <option key={p.partecipante_id} value={p.partecipante_id}>{p.nome}</option>)}
            </select>
            <input data-testid="versamento-mese" type="month" className={inp} value={vers.mese} onChange={(e) => setVers({ ...vers, mese: e.target.value })} />
            <input data-testid="versamento-importo" placeholder="Importo €" className={inp + " w-32"} value={vers.importo} onChange={(e) => setVers({ ...vers, importo: e.target.value })} />
            <input data-testid="versamento-note" placeholder="Note" className={inp} value={vers.note} onChange={(e) => setVers({ ...vers, note: e.target.value })} />
            <button data-testid="versamento-add-button" onClick={addVers} disabled={!vers.mese || !vers.importo || !vers.partecipante_id}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-40 transition-colors duration-150">Registra</button>
          </div>
          {data.versamenti_legacy?.length > 0 && (
            <p className="text-xs text-slate-400 mt-3">
              Versamenti storici non assegnati a un partecipante: {data.versamenti_legacy.length} (contano nel totale versato complessivo, non nei singoli partecipanti).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
